import { filter, takeUntil } from 'rxjs/operators';
import { Router, NavigationEnd } from '@angular/router';
import { DOCUMENT } from '@angular/common';
import {
  Component,
  Inject,
  ElementRef,
  OnInit,
  Renderer2,
  HostListener,
  OnDestroy,
} from '@angular/core';
import { BehaviorSubject, Subject, combineLatest } from 'rxjs';
import { AuthService } from '../../../services/auth.service';
import { Role, RootModule } from '../../../models/role';
import {
  ENVIRONMENT_CONFIG,
  ROUTE_INFO,
} from '../../../config/environment.config.token';
import { RouteInfo } from './sidebar.metadata';
import { UserService } from '../../../services';
import { EnvironmentConfig } from '../../../config/environment.config';

@Component({
  selector: 'meddata-sidebar',
  templateUrl: './sidebar.component.html',
})
export class SidebarComponent implements OnInit, OnDestroy {
  public sidebarItems: RouteInfo[] = [];
  userRoles: string[] = [];
  public roleSidebarItems: any[] = [];
  level1Menu = '';
  level2Menu = '';
  level3Menu = '';
  public innerHeight: any;
  public bodyTag: any;
  listMaxHeight: string;
  listMaxWidth: string;
  userFullName: string;
  hasPerson: boolean;
  isVerified: boolean;
  employeeFullName: string;
  employeeCategory: string;
  userImg: string;
  userContact: any;
  userTypePatient: string;
  userTypeEmployee: string;
  userTypeAdmin: string;
  headerHeight = 60;
  currentRoute: string;
  routerObj = null;
  private currnetRootModuleName: string;
  private destroy$ = new Subject<void>();
  private changeGlobalModul = new BehaviorSubject<string>('');

  constructor(
    @Inject(DOCUMENT) private document: Document,
    @Inject(ROUTE_INFO) private routeInfo: RouteInfo[],
    @Inject(ENVIRONMENT_CONFIG) private en_config: EnvironmentConfig,
    private renderer: Renderer2,
    public elementRef: ElementRef,
    private authService: AuthService,
    private userService: UserService,
    private router: Router
  ) {
    const body = this.elementRef.nativeElement.closest('body');
    this.routerObj = this.router.events.subscribe((event) => {
      if (event instanceof NavigationEnd) {
        // logic for select active menu in dropdown
        const role = ['admin', 'patient-cabinet', 'queue-back', 'queue-front'];
        const currenturl = event.url.split('?')[0];
        const firstString = currenturl.split('/').slice(1)[0];
        const oldGlobalModule = this.currnetRootModuleName;
        this.currnetRootModuleName = firstString;
        if (role.indexOf(firstString) !== -1) {
          this.level1Menu = event.url.split('/')[2];
          this.level2Menu = event.url.split('/')[4] ?? event.url.split('/')[3];
        } else {
          this.currnetRootModuleName = RootModule.Doctor; //Set Default Root Module
          this.level1Menu = event.url.split('/')[1];
          this.level2Menu = event.url.split('/')[2];
        }
        if (oldGlobalModule != this.currnetRootModuleName) {
          this.changeGlobalModul.next(this.currnetRootModuleName);
        }
        // close sidebar on mobile screen after menu select
        this.renderer.removeClass(this.document.body, 'overlay-open');
      }
    });
  }

  @HostListener('window:resize', ['$event'])
  windowResizecall(event) {
    this.setMenuHeight();
    this.checkStatuForResize(false);
  }

  @HostListener('document:mousedown', ['$event'])
  onGlobalClick(event): void {
    if (!this.elementRef.nativeElement.contains(event.target)) {
      this.renderer.removeClass(this.document.body, 'overlay-open');
    }
  }
  callLevel1Toggle(event: any, element: any) {
    if (element === this.level1Menu) {
      this.level1Menu = '0';
    } else {
      this.level1Menu = element;
    }
    const hasClass = event.target.classList.contains('toggled');
    if (hasClass) {
      this.renderer.removeClass(event.target, 'toggled');
    } else {
      this.renderer.addClass(event.target, 'toggled');
    }
  }
  callLevel2Toggle(event: any, element: any) {
    if (element === this.level2Menu) {
      this.level2Menu = '0';
    } else {
      this.level2Menu = element;
    }
  }
  callLevel3Toggle(event: any, element: any) {
    if (element === this.level3Menu) {
      this.level3Menu = '0';
    } else {
      this.level3Menu = element;
    }
  }
  get timeStamp() {
    return this.en_config.env.timeStamp;
  }
  get version() {
    return this.en_config.env.version;
  }
  ngOnInit() {
    this.userService.currentUserContact$
      .pipe(takeUntil(this.destroy$))
      .subscribe((a) => {
        this.userContact = a;
        if (this.userContact != null && this.userContact.imageName != null) {
          this.userImg = `${this.en_config.env.storageUrl}/api/files/${this.userContact.imageName}/download`;
        } else {
          this.userImg = 'assets/images/user/doctor.jpg';
        }
      });
    this.userService.currentPerson$
      .pipe(takeUntil(this.destroy$))
      .subscribe((a) => {
        if (a) {
          this.userFullName = `${a.lastName} ${a.firstName}`;
          this.isVerified = a.isVerified;
          this.hasPerson = !!a.id;
        }
      });
    this.changeGlobalModul
      .pipe(takeUntil(this.destroy$))
      .subscribe(async (s) => {
        this.sidebarItems = await this.asyncFilter(
          this.roleSidebarItems,
          async (x) => {
            return x.rootModuleName === s;
          }
        );
      });
    combineLatest([
      this.authService.currentUser$,
      this.authService.rootPermissionLoaded$,
    ])
      .pipe(
        takeUntil(this.destroy$),
        filter(([user, loaded]) => !!user && loaded)
      )
      .subscribe(async ([user, _]) => {
        let userRoles = Array.isArray(user.role) ? user.role : [user.role];
        this.userRoles = userRoles;

        if (userRoles.includes(Role.Admin)) {
          this.userTypeAdmin = Role.Admin;
        }
        if (userRoles.includes(Role.Patient)) {
          this.userTypePatient = Role.Patient;
        }
        if (userRoles.includes(Role.Doctor)) {
          this.userTypeEmployee = Role.Doctor;
        }
        if (userRoles.includes(Role.TenantAdmin)) {
          this.userTypeAdmin = Role.TenantAdmin;
        }
        await this.drawLeftmenu(userRoles);
      });
    // this.authService.rootPermissionLoaded$
    //   .pipe(takeUntil(this.destroy$))
    //   .subscribe((a) => {
    //     if (a) {
    //       this.drawLeftmenu(this.userRoles);
    //     }
    //   });
    // this.authService.currentUser$
    //   .pipe(takeUntil(this.destroy$))
    //   .subscribe(async (user) => {
    //     let userRoles = [];
    //     if (typeof user.role === 'string') {
    //       userRoles.push(user.role);
    //     } else {
    //       userRoles = user.role;
    //     }
    //     userRoles = userRoles ?? [];
    //     this.userRoles = userRoles;

    //     //this.userImg ='assets/images/user/doctor.jpg';
    //     //draw left menu
    //     await this.drawLeftmenu(userRoles);

    //     if (userRoles.includes(Role.Admin)) {
    //       this.userTypeAdmin = Role.Admin;
    //     }
    //     if (userRoles.includes(Role.Patient)) {
    //       this.userTypePatient = Role.Patient;
    //     }
    //     if (userRoles.includes(Role.Doctor)) {
    //       this.userTypeEmployee = Role.Doctor;
    //     }
    //     if (userRoles.includes(Role.TenantAdmin)) {
    //       this.userTypeAdmin = Role.TenantAdmin;
    //     }
    //   });

    this.initLeftSidebar();
    this.bodyTag = this.document.body;
    this.userService.currentEmployeeSubject$.subscribe((a) => {
      if (a) {
        this.employeeFullName = `${a.lastName} ${a.firstName}`;
        this.employeeCategory = `${a.categoryName}`;
      }
    });
  }
  async validator(someObject: any): Promise<boolean> {
    return await this.authService.hasPermission(someObject);
  }
  async drawLeftmenu(userRoles) {
    this.sidebarItems = await this.asyncFilter(
      this.routeInfo,
      async (x: RouteInfo) => {
        return (
          x.role.some((r) => userRoles.includes(r)) &&
          (x.permission
            ? await this.authService.hasPermission(x.permission)
            : true) &&
          (x.andPermission
            ? await this.authService.hasPermission(x.andPermission)
            : true)
        );
      }
    );

    for (let index = 0; index < this.sidebarItems.length; index++) {
      const a = this.sidebarItems[index];
      if (a.submenu && a.submenu.length > 0) {
        a.submenu = await this.asyncFilter(a.submenu, async (x: RouteInfo) => {
          return (
            (x.permission
              ? await this.authService.hasPermission(x.permission)
              : true) &&
            (x.andPermission
              ? await this.authService.hasPermission(x.andPermission)
              : true)
          );
        });
        //sub sub
        for (let index = 0; index < a.submenu.length; index++) {
          const b = a.submenu[index];
          if (b.submenu && b.submenu.length > 0) {
            b.submenu = await this.asyncFilter(
              b.submenu,
              async (x: RouteInfo) => {
                return (
                  (x.permission
                    ? await this.authService.hasPermission(x.permission)
                    : true) &&
                  (x.andPermission
                    ? await this.authService.hasPermission(x.andPermission)
                    : true)
                );
              }
            );
          }
        }
      }
    }
    this.roleSidebarItems = this.sidebarItems;
    this.changeGlobalModul.next(this.currnetRootModuleName);
  }
  ngOnDestroy() {
    this.routerObj.unsubscribe();
    this.destroy$.next(null);
    this.destroy$.complete();
  }
  initLeftSidebar() {
    const _this = this;
    // Set menu height
    _this.setMenuHeight();
    _this.checkStatuForResize(true);
  }

  setMenuHeight() {
    this.innerHeight = window.innerHeight;
    const height = this.innerHeight - this.headerHeight;
    this.listMaxHeight = height + '';
    this.listMaxWidth = '500px';
  }
  isOpen() {
    return this.bodyTag.classList.contains('overlay-open');
  }
  checkStatuForResize(firstTime) {
    if (window.innerWidth < 1170) {
      this.renderer.addClass(this.document.body, 'ls-closed');
    } else {
      this.renderer.removeClass(this.document.body, 'ls-closed');
    }
  }
  mouseHover(e) {
    const body = this.elementRef.nativeElement.closest('body');
    if (body.classList.contains('submenu-closed')) {
      this.renderer.addClass(this.document.body, 'side-closed-hover');
      this.renderer.removeClass(this.document.body, 'submenu-closed');
    }
  }
  mouseOut(e) {
    const body = this.elementRef.nativeElement.closest('body');
    if (body.classList.contains('side-closed-hover')) {
      this.renderer.removeClass(this.document.body, 'side-closed-hover');
      this.renderer.addClass(this.document.body, 'submenu-closed');
    }
  }
  logout() {
    this.authService.logout();
  }
  onIdentityPerson() {
    this.authService.onIdentityPersonSubject.next(true);
  }

  asyncFilter = async (arr, predicate) =>
    arr.reduce(
      async (memo, e) => ((await predicate(e)) ? [...(await memo), e] : memo),
      []
    );
}
